/**
 * PresetSaveDialog Component
 * Dialog for saving instrument presets
 */

import { useState, useEffect, useRef } from 'react';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentParameters } from '../../types/instruments';
import { instrumentPresetManager } from '../../services/instruments/InstrumentPresetManager';
import './PresetDialogs.css';

/**
 * Props for PresetSaveDialog component
 */
interface PresetSaveDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Current instrument type */
  instrumentId: InstrumentType;
  /** Current parameters to save */
  parameters: InstrumentParameters;
  /** Callback when preset is saved */
  onSave: (presetId: string) => void;
}

/**
 * PresetSaveDialog component
 * Allows users to save current instrument parameters as a preset
 */
export function PresetSaveDialog({
  isOpen,
  onClose,
  instrumentId,
  parameters,
  onSave,
}: PresetSaveDialogProps) {
  const [presetName, setPresetName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setPresetName('');
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle save
  const handleSave = () => {
    if (!presetName.trim()) {
      setError('Please enter a preset name');
      return;
    }

    try {
      const newPreset = instrumentPresetManager.savePreset({
        instrumentId,
        name: presetName.trim(),
        description: '',
        parameters,
      });
      onSave(newPreset.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preset');
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="preset-dialog-overlay" onClick={onClose}>
      <div
        className="preset-dialog"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="preset-dialog-header">
          <h2 className="preset-dialog-title">Save Preset</h2>
          <button
            className="preset-dialog-close"
            onClick={onClose}
            aria-label="Close dialog"
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="preset-dialog-content">
          {error && (
            <div className="preset-dialog-error" role="alert">
              {error}
            </div>
          )}

          <div className="preset-dialog-field">
            <label htmlFor="preset-name" className="preset-dialog-label">
              Preset Name
            </label>
            <input
              ref={inputRef}
              id="preset-name"
              type="text"
              className="preset-dialog-input"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
              }}
              placeholder="Enter preset name"
              maxLength={50}
            />
          </div>

          <div className="preset-dialog-actions">
            <button
              type="button"
              className="preset-btn preset-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="preset-btn preset-btn-primary"
              onClick={handleSave}
              disabled={!presetName.trim()}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
